import { gql } from '@apollo/client';
import { OUTCOME_FIELDS } from './businessOutcomesFragments';
import { MILESTONE_FIELDS } from './milestonesFragments';
import { RISK_FIELDS } from './risksFragments';
import { STAKEHOLDER_FIELDS } from './stakeholdersFragments';
import { ASSUMPTION_FIELDS } from './assumptionsFragments';

export const GET_PROJECTS_DASHBOARD = gql`
  query GetProjectsDashboard($searchString: String) {
    baseProjects(searchString: $searchString) {
      id
      legacyId
      name
      alias
      projectManager
      team {
        id
        members {
          employeeAlias
          role
          fte
          isBench
          isBackup
        }
      }
      description
      isArchived
      projectState
      accountData {
        accountName
        accountId
        ownerAlias
      }
      auditData {
        lastUpdate
        lastReportDate
      }
      resourcesData {
        externalResources {
          name
          value
        }
      }
      health {
        id
        projectId
        zone
        healthChecks {
          id
          name
          message
          targetZone
          status
          createdAt
        }
        createdAt
      }
      staffingRisk {
        id
        projectId
        priority
        issues {
          id
          description
          status
          targetPriority
          systemIssueType
          staffingRiskId
          createdAt
          updatedAt
        }
      }
      budget {
        billingType
        endDate
      }
      accessInfo {
        id
        sections {
          section
          allowedActions
        }
      }
      projectDimension {
        engagementType
        commercialState
      }
    }
  }
`;

export const GET_DASHBOARD_PROJECT_DETAILS = gql`
  query GetDashboardProjectDetails($id: ID!) {
    baseProject(id: $id) {
      id
      legacyId
      name
      alias
      projectManager
      projectDimension {
        engagementType
        commercialState
      }
      accountData {
        sowId
        accountName
        accountId
        isAccountCodeName
        ownerAlias
      }
      auditData {
        lastUpdate
        lastReportDate
        archivedAt
        activatedAt
      }
      budget {
        billingType
        fte
        endDate
      }
      resourcesData {
        externalResources {
          name
          value
        }
      }
      risks {
        id
        projectId
        alias
        title
        status
        probability
        type
        severity
        totalScore
        riskOwner
        timeline {
          id
          text
          createdBy
          createdAt
        }
        mitigationStrategy
        isEscalated
        isIncludedInCustomerReport
        resolvedAt
        resolveComment
        createdBy
        createdAt
        updatedBy
        updatedAt
      }
      milestones {
        id
        alias
        title
        description
        type
        status
        businessOutcomes
        initialPlannedDate
        currentPlannedDate
        completionDate
        rejectionDate
        comment
        rejectionReason
      }
      businessOutcomes {
        id
        alias
        name
        description
        status
        milestones
      }
      health {
        id
        projectId
        zone
        healthChecks {
          id
          name
          message
          targetZone
          status
          zoneSince
          createdAt
        }
        createdAt
      }
      engineeringPolicy {
        id
        productionOwnership
      }
      staffingRisk {
        id
        projectId
        priority
        issues {
          id
          description
          status
          targetPriority
          systemIssueType
          staffingRiskId
          createdAt
          updatedAt
        }
      }
      settings {
        forecastSettings {
          isProjectForecastTrackingEnabled
          isEvmTrackingEnabled
        }
      }
      forecast {
        id
        projectId
        plannedProgress
        actualProgress
        forecastCompletionBudgetHours
        forecastCompletionDate
      }
      budget {
        id
        extendedEndDate
        structure {
          id
          hours
          status
          source
        }
      }
      team {
        id
        members {
          employeeAlias
          role
          fte
          isBench
          isBackup
        }
      }
      accessInfo {
        id
        sections {
          section
          allowedActions
        }
      }
    }
  }
`;

export const GET_PROJECT_INITIATION_DATA_BY_RAW_PROJECT_ID = gql`
  query GetProjectInitiationDataByRawProjectId($rawProjectId: ID!) {
    baseProject(id: $rawProjectId) {
      id
      legacyId
      name
      projectDimension {
        engagementType
      }
      accountData {
        accountName
        accountId
      }
      sensitiveData {
        originalName
      }
      team {
        id
        expectedStructure {
          id
          positionId
          technologyId
          competencyLevel
          requiredFte
          requiredRole
          description
        }
      }
      engagement {
        id
        crmId
        name
        accountId
        expectedStartDate
        status
        relatedProjectId
        rawProjectId
        deletedAt
      }
      settings {
        forecastSettings {
          isProjectForecastTrackingEnabled
          isEvmTrackingEnabled
        }
      }
    }
  }
`;

export const GET_PROJECT_DETAILS = gql`
  query GetProjectDetails($id: ID!) {
    baseProject(id: $id) {
      id
      legacyId
      name
      alias
      projectManager
      isArchived
      projectState
      projectDimension {
        engagementType
        commercialState
      }
      description
      accountData {
        sowId
        accountName
        accountId
        isAccountCodeName
        ownerAlias
        isPortalEnabled
      }
      budgetData {
        defaultBudgetId
        availableBudgets
        assignmentForbidden
      }
      auditData {
        lastReportDate
        lastUpdate
        archivedAt
        activatedAt
      }
      sensitiveData {
        originalName
        azureGroups {
          azureId
          key
          name
        }
        onePasswordVaults {
          vaultId
          key
          name
        }
      }
      resourcesData {
        externalResources {
          name
          value
        }
        toolsInitState
      }
      settings {
        reportSettings {
          reportFrequency
          isSystemReportUsed
          approvalType
        }
        cycleActivitySettings {
          cycleFrequency
          cycleStartDay
          cycleEndDay
          planningDay
          demoDay
          cycleSettingsStatus
          cycleChangeReason
          cycleRejectionReason
        }
        forecastSettings {
          isProjectForecastTrackingEnabled
          isEvmTrackingEnabled
        }
      }
      forecast {
        id
        projectId
        plannedProgress
        actualProgress
        forecastCompletionBudgetHours
        forecastCompletionDate
      }
      cycleActivities {
        id
        projectId
        title
        startDate
        endDate
        status
        planning {
          id
          cycleActivityId
          planningDate
          details
          plannedScopeLink
          isSkipped
          skipReason
        }
        demo {
          id
          cycleActivityId
          demoDate
          details
          demoRecordLink
          isSkipped
          skipReason
        }
      }
      health {
        id
        projectId
        zone
        healthChecks {
          id
          healthId
          name
          message
          targetZone
          status
          createdAt
        }
        createdAt
        updatedAt
      }
      staffingRisk {
        id
        projectId
        priority
        issues {
          id
          description
          status
          targetPriority
          systemIssueType
          staffingRiskId
          createdAt
          updatedAt
        }
        followUps {
          id
          description
          followUpDate
          status
          createdAt
          updatedAt
        }
      }
      budget {
        id
        projectName
        billingType
        startDate
        endDate
        sowId
        structure {
          id
          hours
        }
      }
      team {
        id
        members {
          employeeAlias
          role
          fte
          isBench
          isBackup
        }
      }
      accessInfo {
        id
        sections {
          section
          allowedActions
        }
      }
    }
  }
`;

export const GET_PREDECESSOR_CANDIDATES = gql`
  query GetPredecessorCandidates($searchString: String) {
    baseProjects(searchString: $searchString) {
      id
      name
      accountData {
        accountId
      }
      health {
        id
        createdAt
      }
      milestones {
        id
        status
        type
      }
      businessOutcomes {
        id
        status
      }
      risks {
        id
        status
      }
      stakeholders {
        id
      }
      assumptions {
        id
        status
      }
    }
  }
`;

export const GET_PREDECESSOR_SOURCE = gql`
  query GetPredecessorSource($searchString: String) {
    baseProjects(searchString: $searchString) {
      id
      health {
        id
        createdAt
      }
      businessOutcomes {
        ${OUTCOME_FIELDS}
      }
      milestones {
        ${MILESTONE_FIELDS}
      }
      risks {
        ${RISK_FIELDS}
        description
        mitigationStrategy
      }
      stakeholders {
        ${STAKEHOLDER_FIELDS}
      }
      assumptions {
        ${ASSUMPTION_FIELDS}
      }
    }
  }
`;
